interface HoursBreakdownCardProps {
  customerHours: number
  rdHours: number
  utilizationPct: number | null
}

function formatHours(h: number): string {
  if (h >= 1000) return `${(h / 1000).toFixed(1)}k h`
  return `${h.toFixed(0)} h`
}

export function HoursBreakdownCard({
  customerHours,
  rdHours,
  utilizationPct,
}: HoursBreakdownCardProps) {
  const total = customerHours + rdHours
  const customerPct = total > 0 ? (customerHours / total) * 100 : 0
  const rdPct = total > 0 ? (rdHours / total) * 100 : 0

  return (
    <div
      className="rounded-2xl border border-white/[0.07] bg-[#0d1321]/70 backdrop-blur-sm p-5 h-full shadow-[0_4px_24px_rgba(0,0,0,0.4)]"
      data-testid="hours-breakdown-card"
    >
      {/* Header */}
      <div className="flex items-center gap-2 mb-5">
        <div className="h-3.5 w-0.5 rounded-full bg-gradient-to-b from-indigo-500 to-purple-500" />
        <p className="section-label">Ore produzione</p>
      </div>

      {total === 0 ? (
        <p className="text-sm text-[--km-text-muted]">Nessun dato ore disponibile.</p>
      ) : (
        <>
          {/* Stacked bar segmentata */}
          <div className="h-2.5 w-full rounded-full overflow-hidden flex mb-5 bg-[#182038]">
            <div
              className="bg-gradient-to-r from-indigo-600 to-indigo-400 h-full transition-all duration-700"
              style={{ width: `${customerPct}%` }}
              role="img"
              aria-label={`Ore cliente: ${customerPct.toFixed(0)}%`}
            />
            <div
              className="bg-gradient-to-r from-purple-600 to-purple-400 h-full transition-all duration-700"
              style={{ width: `${rdPct}%` }}
              role="img"
              aria-label={`Ore R&D: ${rdPct.toFixed(0)}%`}
            />
          </div>

          {/* Legenda */}
          <div className="space-y-3">
            <div className="flex items-center justify-between p-2.5 rounded-xl bg-white/[0.02] hover:bg-white/[0.04] transition-colors">
              <span className="flex items-center gap-2 text-xs text-[--km-text-secondary] font-medium">
                <span className="inline-block h-2 w-2 rounded-full bg-gradient-to-br from-indigo-500 to-indigo-400" />
                Ore cliente
              </span>
              <div className="text-right">
                <span className="text-sm font-bold text-[--km-text-primary] tabular-nums">
                  {formatHours(customerHours)}
                </span>
                <span className="text-xs text-[--km-text-muted] ml-1.5">
                  {customerPct.toFixed(0)}%
                </span>
              </div>
            </div>

            <div className="flex items-center justify-between p-2.5 rounded-xl bg-white/[0.02] hover:bg-white/[0.04] transition-colors">
              <span className="flex items-center gap-2 text-xs text-[--km-text-secondary] font-medium">
                <span className="inline-block h-2 w-2 rounded-full bg-gradient-to-br from-purple-500 to-purple-400" />
                Ore R&D / overhead
              </span>
              <div className="text-right">
                <span className="text-sm font-bold text-[--km-text-primary] tabular-nums">
                  {formatHours(rdHours)}
                </span>
                <span className="text-xs text-[--km-text-muted] ml-1.5">
                  {rdPct.toFixed(0)}%
                </span>
              </div>
            </div>
          </div>

          {/* Separatore + totale + utilization */}
          <div className="mt-4 pt-4 border-t border-white/[0.05] space-y-2">
            <div className="flex items-center justify-between text-xs">
              <span className="text-[--km-text-muted] font-medium">Totale ore</span>
              <span className="font-bold text-[--km-text-primary] tabular-nums">
                {formatHours(total)}
              </span>
            </div>

            {utilizationPct !== null && (
              <div className="flex items-center justify-between text-xs">
                <span className="text-[--km-text-muted] font-medium">Utilization rate</span>
                <span className={`font-bold tabular-nums ${
                  utilizationPct >= 75 ? 'text-emerald-400'
                    : utilizationPct >= 60 ? 'text-amber-400' : 'text-red-400'
                }`}>
                  {utilizationPct.toFixed(1)}%
                </span>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )
}
